import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "../components/DataTable";
import { EmptyState, PageHeader, StatusPill, type StatusTone } from "../components/primitives";
import { IconCampaigns, IconCheck } from "../components/icons";
import { apiFetch, type IssueRow } from "../lib/api";
import {
  createBroadcast, fetchAudienceSize, fetchBroadcasts, rate,
  type Broadcast, type BroadcastStatus, type DeliveryStatus,
} from "../lib/api-broadcasts";
import { ammanStamp } from "../lib/dates";
import { useAudienceInterests } from "../lib/use-audience-interests";

/**
 * Broadcasts: send one approved newsletter to everyone who follows its topics.
 *
 * The list is every broadcast with its real results, read from our own send
 * engine. "New broadcast" picks the newsletter and its audience; the email is
 * frozen at that moment and opened on its own page to test and schedule.
 */

export function broadcastPill(status: BroadcastStatus): { tone: StatusTone; label: string } {
  switch (status) {
    case "sent": return { tone: "success", label: "Sent" };
    case "sending": return { tone: "active", label: "Sending" };
    case "scheduled": return { tone: "active", label: "Scheduled" };
    case "cancelled": return { tone: "neutral", label: "Cancelled" };
    case "failed": return { tone: "error", label: "Failed" };
    default: return { tone: "neutral", label: "Draft" };
  }
}

export function useTopicLabel(): (value: string) => string {
  const interests = useAudienceInterests();
  return (value) => {
    const c = interests.choices.find((x) => x.value === value);
    return c?.labelAr || c?.label || value;
  };
}

/**
 * What stands between this tool and a subscriber's inbox, as a checklist the
 * team can act on. It disappears once everything is in place.
 */
export function SetupChecklist({ delivery }: { delivery: DeliveryStatus | undefined }) {
  if (!delivery || delivery.ready) return null;
  const missing = new Set(delivery.missing);
  const steps = [
    { done: !missing.has("the database connection"), label: "Database connected" },
    { done: !missing.has("Amazon SES keys"), label: "Amazon SES connected", how: "Ahmad adds the SES keys to Cloudflare" },
    { done: !missing.has("a verified sending address"), label: "Sending address verified", how: "e.g. newsletter@news.altibbi.com, verified in SES" },
    { done: !missing.has("sending to be switched on"), label: "Sending switched on", how: "the last step, once the others are done" },
  ];
  return (
    <section className="bc-setup" aria-label="Before newsletters can reach subscribers">
      <h2 className="nl-panel-title">Before anything reaches subscribers</h2>
      <p className="nl-panel-sub">You can already build broadcasts and automations and send yourself tests.</p>
      <ol>
        {steps.map((s) => (
          <li key={s.label} className={s.done ? "is-done" : ""}>
            <span className="bc-setup-mark" aria-hidden>{s.done ? <IconCheck /> : null}</span>
            <span>
              <strong>{s.label}</strong>
              {!s.done && s.how && <em>{s.how}</em>}
            </span>
          </li>
        ))}
      </ol>
      <p className="bc-hint">Step-by-step guide: docs/BROADCASTS.md in the project.</p>
    </section>
  );
}

/**
 * The topics a subscriber can follow, as toggles. Loading and failure are
 * said in words, with a way to try again: an empty row with nothing to press
 * left the form with no way forward.
 */
export function TopicPicker({ value, onChange }: { value: string[]; onChange: (next: string[]) => void }) {
  const interests = useAudienceInterests();
  const toggle = (v: string) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  if (interests.status === "loading") return <p className="bc-hint">Loading topics…</p>;
  if (interests.status === "error" || interests.choices.length === 0) {
    return (
      <p className="bc-hint">
        {interests.status === "error" ? "Topics couldn't be loaded." : "No topics yet: nobody has subscribed to any."}{" "}
        <button type="button" className="nl-text-btn" onClick={interests.retry}>Try again</button>
      </p>
    );
  }
  return (
    <div className="bc-topics" role="group" aria-label="Topics">
      {interests.choices.map((c) => (
        <button key={c.value} type="button" className="ui-pill" aria-pressed={value.includes(c.value)} onClick={() => toggle(c.value)}>
          <span lang="ar" dir="rtl">{c.labelAr || c.label}</span>
        </button>
      ))}
    </div>
  );
}

export function BroadcastsPage() {
  const [rows, setRows] = useState<Broadcast[] | null>(null);
  const [delivery, setDelivery] = useState<DeliveryStatus>();
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const topic = useTopicLabel();

  useEffect(() => {
    fetchBroadcasts()
      .then(({ data, meta }) => { setRows(data); setDelivery(meta.delivery); })
      .catch((e: Error) => setError(e.message));
  }, []);

  const columns = useMemo<ColumnDef<Broadcast>[]>(() => [
    {
      accessorKey: "name", header: "Newsletter",
      cell: (c) => <Link className="bc-name" to={`/broadcasts/${c.row.original.id}`}>{c.row.original.name}</Link>,
    },
    {
      accessorKey: "status", header: "Status",
      cell: (c) => {
        const p = broadcastPill(c.row.original.status);
        return <StatusPill tone={p.tone}>{p.label}</StatusPill>;
      },
    },
    {
      id: "audience", header: "Audience",
      cell: (c) => <span lang="ar" dir="rtl">{c.row.original.categories.map(topic).join("، ")}</span>,
    },
    {
      id: "recipients", header: "Recipients",
      accessorFn: (r) => r.totals?.recipients ?? r.recipient_count,
      cell: (c) => <span className="tabnum">{Number(c.getValue() ?? 0).toLocaleString("en-US")}</span>,
    },
    {
      id: "opened", header: "Opened",
      cell: (c) => <span className="tabnum">{rate(c.row.original.totals?.opened, c.row.original.totals?.sent)}</span>,
    },
    {
      id: "clicked", header: "Clicked",
      cell: (c) => <span className="tabnum">{rate(c.row.original.totals?.clicked, c.row.original.totals?.sent)}</span>,
    },
    {
      id: "when", header: "When",
      accessorFn: (r) => r.sent_at ?? r.scheduled_at ?? r.created_at,
      cell: (c) => <span className="tabnum">{ammanStamp(c.getValue() as string)}</span>,
    },
  ], [topic]);

  return (
    <>
      <PageHeader
        title="Broadcasts"
        meta="Send an approved newsletter to everyone who follows its topics."
        // One solid blue per screen: while the form is open, its button is it.
        actions={creating ? undefined : (
          <button type="button" className="nl-btn-primary" onClick={() => setCreating(true)}>
            New broadcast
          </button>
        )}
      />
      <SetupChecklist delivery={delivery} />
      {creating && <NewBroadcast onClose={() => setCreating(false)} />}

      {error ? (
        <EmptyState title="Broadcasts could not be loaded" body={error} hint="Check the connection and reload the page." />
      ) : rows === null ? (
        <p className="bc-loading">Loading broadcasts…</p>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<IconCampaigns width={22} height={22} />}
          title="No broadcasts yet"
          body="A broadcast is one newsletter sent to everyone who follows its topics. Send an approved newsletter by hand, or let an automation build one every week."
          hint="Every send — by hand or automatic — shows here with its opens, clicks and unsubscribes."
          actions={<Link className="nl-btn-secondary" to="/automations">Set up a weekly automation</Link>}
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          ariaLabel="Broadcasts"
          searchPlaceholder="Search broadcasts…"
          initialSort={[{ id: "when", desc: true }]}
          getRowId={(r) => r.id}
        />
      )}
    </>
  );
}

/** Choose the newsletter and who gets it. The email is built when this is confirmed. */
function NewBroadcast({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [issues, setIssues] = useState<IssueRow[] | null>(null);
  const [issueId, setIssueId] = useState("");
  const [categories, setCategories] = useState<string[]>([]);
  const [size, setSize] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch("/api/v1/issues")
      .then((r) => r.json())
      .then((b: { data?: IssueRow[] }) => setIssues((b.data ?? []).filter((i) => i.status === "approved")))
      .catch(() => setIssues([]));
  }, []);

  const issue = issues?.find((i) => i.id === issueId) ?? null;
  // The audience starts as the newsletter's own topics.
  useEffect(() => { setCategories(issue?.categories ?? []); }, [issue]);
  useEffect(() => {
    if (!categories.length) { setSize(0); return; }
    let live = true;
    setSize(null);
    fetchAudienceSize(categories).then(({ data }) => { if (live) setSize(data.total); }).catch(() => { if (live) setSize(null); });
    return () => { live = false; };
  }, [categories]);


  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await createBroadcast(issueId, categories);
      navigate(`/broadcasts/${data.id}`);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <section className="bc-new" aria-label="New broadcast">
      <div className="bc-new-row">
        <label className="bc-field">
          <span>Newsletter</span>
          <select className="nl-builder-select" value={issueId} onChange={(e) => setIssueId(e.target.value)}>
            <option value="">{issues === null ? "Loading…" : issues.length ? "Choose an approved newsletter" : "No approved newsletters yet"}</option>
            {(issues ?? []).map((i) => <option key={i.id} value={i.id}>{i.title}</option>)}
          </select>
        </label>
      </div>

      {issue && (
        <div className="bc-field">
          <span>Send to people who follow</span>
          <TopicPicker value={categories} onChange={setCategories} />
        </div>
      )}

      {error && <p className="bc-error" role="alert">{error}</p>}

      <div className="bc-new-actions">
        <span className="bc-size">
          {issue && (size === null ? "Counting…" : `${size.toLocaleString("en-US")} recipients`)}
        </span>
        <button type="button" className="nl-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="nl-btn-primary" onClick={create}
          disabled={busy || !issue || !categories.length || !size}>
          {busy ? "Building the email…" : "Create broadcast"}
        </button>
      </div>
    </section>
  );
}
